/*
 * Represents the models that compose the performance table wrapper component.
 */

import { BeaconTableHeader } from '@trp-beacon-ui-kit/foundation/types/components';
import { Currency } from '../../common/enums/currency-enum';
import { ShareClassEnum } from '../../common/enums/share-class-enum';
import { Constants } from './product-center-table-wrapper.constants';

export enum TableRecordVariant {
  TEXT = 'text',
  NUMERAL = 'numeral',
  DATE = 'date',
  ICONS = 'icons',
}

export enum DisclaimerPositioning {
  FOOTER = 'FOOTER',
  PRICE = 'PRICE',
  NOTICE = 'NOTICE',
  PRODUCT_PROFILE_TOOL_TIP = 'PRODUCT_PROFILE_TOOL_TIP',
  TARGET_MARKET_DETERMINATION_TOOL_TIP = 'TARGET_MARKET_DETERMINATION_TOOL_TIP',
}

export type ProductCenterExhibit = {
  products: Product[];
  disclaimers: Disclaimer[] | null;
};

export type Disclaimer = {
  disclaimerText: {
    html: string;
  };
  positioning: DisclaimerPositioning;
};

export type Product = {
  isChecked?: boolean;
  productCode: string;
  parentProductCode: string | null;
  productName: ProductName;
  disclaimers: Disclaimer[] | null;
  performance?: Performance;
  pricing?: Pricing;
  esg?: ESG;
  documents?: Documents;
  cumulativeChart?: CumulativeChart | null;
};

export type ProductName = {
  apir: string | null;
  shareClass: ShareClassEnum | null;
  shareClassTerm?: string | null;
  baseCurrencyName: Currency;
  fundLink: string;
  fundName: string;
  vehicleTypeName: string;
  isin?: string | null;
  sfdrClassification?: string | null;
  investmentStrategiesAssetClassName?: string | null;
  investmentStrategiesRegionName?: string | null;
  declarationFrequencyName?: string | null;
};

export type Performance = {
  inceptionDate: string;
  performanceAsOfDate: string;
  morningstarMedalistRating: string | null;
  fundReturns: FundReturns;
};

export type FundReturns = {
  oneYear: FundReturnsValue[] | null;
  threeYears: FundReturnsValue[] | null;
  fiveYears: FundReturnsValue[] | null;
  tenYears: FundReturnsValue[] | null;
  sinceInception: FundReturnsValue[] | null;
  yearToDate: FundReturnsValue[] | null;
};

export type FundReturnsValue = {
  feeType: string | null;
  value: number | null;
};

export type Pricing = {
  applicationPrice: number | null;
  withdrawalPrice: number | null;
  navPrice: number | null;
  priceAsOfDate: string | null;
  performanceReportURL: string | null;
  quarterlyReportURL: string | null;
};

export type ESG = {
  proxyVotingSummary: string | null;
  carbonFootprintSummary: string | null;
  esgEngagementReport: string | null;
  esgImpactAnnualReport: string | null;
};

export type Documents = {
  productDisclosureStatement: string | null;
  targetMarketDetermination: string | null;
  fundProfile: string | null;
};

export type CumulativeChart = {
  benchmarks: Benchmark[] | null | undefined;
  data: CumulativeFundReturns;
};

export type Benchmark = {
  name: string | null;
};

export type CumulativeFundReturns = {
  oneYear?: CumulativeDataPoint[] | null;
  threeYears?: CumulativeDataPoint[] | null;
  fiveYears?: CumulativeDataPoint[] | null;
  tenYears?: CumulativeDataPoint[] | null;
  sinceInception?: CumulativeDataPoint[] | null;
  yearToDate?: CumulativeDataPoint[] | null;
};

export type CumulativeDataPoint = {
  asOfDate: string | null;
  fundValue: number | null;
  benchmarkValues: number[] | null;
};

export type CurrentSort = {
  tableHeaderEl: BeaconTableHeader;
  columnId: string | undefined;
};

// Enums for Chart/Tile view
export enum View {
  TABLE = 'Table',
  CHART_TILES = 'Chart',
}

export enum ToggleClass {
  TOGGLE_ON = 'toggle-on',
  TOGGLE_OFF = 'toggle-off',
}

export enum SortField {
  FUND_NAME = 'fundName',
  APIR = 'apir',
}

export enum SortDirection {
  ASC = 'asc',
  ASCENDING = 'ascending',
  DSC = 'dsc',
  DESCENDING = 'descending',
}

export enum SortAction {
  SORT_BY = 'sort-by',
  SORT_DIRECTION = 'sort-direction',
}

export enum FundReturnKey {
  YEAR_TO_DATE = 'yearToDate',
  ONE_YEAR = 'oneYear',
  THREE_YEARS = 'threeYears',
  FIVE_YEARS = 'fiveYears',
  TEN_YEARS = 'tenYears',
  SINCE_INCEPTION = 'sinceInception',
}

export type SelectedTileTab =
  (typeof Constants)['TILES_TAB_INDEX'][keyof (typeof Constants)['TILES_TAB_INDEX']];

export type SelectedTableTab =
  (typeof Constants)['TABLE_TAB_INDEX'][keyof (typeof Constants)['TABLE_TAB_INDEX']];
